import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { MessageCreditsPanel } from '@/components/messaging/message-credits-panel';
import {
  PurchaseCreditsDialog,
  type CreditsSummary,
} from '@/components/messaging/purchase-credits-dialog';
import { SmsSendingOptionsCard } from '@/components/messaging/sms-sending-options-card';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  isElevationCancelled,
  useElevateSession,
} from '@/hooks/use-elevate-session';
import { useMessagingCreditsCheckoutReturn } from '@/hooks/use-messaging-credits-checkout-return';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDateTime } from '@/utils/format';

interface HubtelStatus {
  provider: 'hubtel';
  isConfigured: boolean;
  paymentsEnabled: boolean;
  messagingEnabled: boolean;
  sms: {
    id: string | null;
    mode: 'byok' | 'trinity';
    status: string;
    senderId: string | null;
    maskedHint: string | null;
    lastValidatedAt: string | null;
    isConfigured: boolean;
    hasChurchCredentials: boolean;
  } | null;
}

type SmsSettingsSection = 'credits' | 'connection';

const SMS_SETTINGS_CREDITS_PATH = '/settings/hubtel/credits';
const SMS_SETTINGS_CONNECTION_PATH = '/settings/hubtel/connection';

const sectionFromPath = (pathname: string): SmsSettingsSection =>
  pathname.endsWith('/connection') ? 'connection' : 'credits';

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="grid gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body-sm text-fg">{value}</dd>
  </div>
);

const ServiceCardSkeleton = () => (
  <Card>
    <CardHeader className="space-y-3">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-full max-w-sm" />
    </CardHeader>
    <CardContent className="space-y-3">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </CardContent>
    <CardFooter>
      <Skeleton className="h-9 w-32" />
    </CardFooter>
  </Card>
);

export const HubtelSettingsPage = () => {
  const { t } = useTranslation('admin');
  const { t: tMessaging } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = sectionFromPath(location.pathname);

  const { elevateAndRetry, requireElevationThen, dialog: sessionDialogs } = useElevateSession();

  const [smsOpen, setSmsOpen] = useState(false);
  const [smsClientId, setSmsClientId] = useState('');
  const [smsClientSecret, setSmsClientSecret] = useState('');
  const [senderId, setSenderId] = useState('');
  const [creditsPurchaseOpen, setCreditsPurchaseOpen] = useState(false);

  useMessagingCreditsCheckoutReturn();

  const openBuyCredits = useCallback(() => {
    setCreditsPurchaseOpen(true);
  }, []);

  useEffect(() => {
    if (searchParams.get('purchase') !== '1') return;
    setCreditsPurchaseOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('purchase');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const status = useQuery({
    queryKey: ['integrations', 'hubtel'],
    queryFn: () => api.get<HubtelStatus>('/integrations/hubtel'),
  });

  const creditsSummary = useQuery({
    queryKey: ['messaging', 'credits'],
    queryFn: () => api.get<CreditsSummary>('/messaging/credits'),
    enabled: Boolean(status.data?.messagingEnabled),
  });

  const invalidateHubtel = () => {
    void queryClient.invalidateQueries({ queryKey: ['integrations', 'hubtel'] });
    void queryClient.invalidateQueries({ queryKey: ['integrations', 'hubtel', 'sms-profile'] });
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'sms-connection'] });
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
  };

  const saveSms = useMutation({
    mutationFn: () =>
      elevateAndRetry(
        () =>
          api.post<HubtelStatus>('/integrations/hubtel', {
            smsClientId,
            smsClientSecret,
            senderId,
            environment: 'live',
          }),
        { description: t('hubtel.elevateSmsDescription') },
      ),
    onSuccess: () => {
      toast.success(t('hubtel.savedSms'));
      setSmsOpen(false);
      setSmsClientSecret('');
      invalidateHubtel();
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const data = status.data;
  const smsConfigured = Boolean(data?.sms?.hasChurchCredentials);
  const canSaveSms =
    Boolean(smsClientId.trim() && smsClientSecret.trim()) &&
    Boolean(senderId.trim() && /^[A-Za-z0-9]+$/.test(senderId.trim()));

  const openSms = () => {
    requireElevationThen(() => {
      setSmsClientId('');
      setSmsClientSecret('');
      setSenderId(data?.sms?.senderId ?? '');
      setSmsOpen(true);
    }, t('hubtel.elevateSmsDescription'));
  };

  const smsBadge = !data?.sms?.hasChurchCredentials
    ? { label: t('hubtel.smsNotConnected'), variant: 'neutral' as const }
    : data.sms.mode === 'byok' && data.sms.isConfigured
      ? { label: t('hubtel.smsReady'), variant: 'success' as const }
      : { label: t('hubtel.smsNotUsingAccount'), variant: 'info' as const };

  const showMessaging = Boolean(data?.messagingEnabled);
  const smsUsingHubtel =
    Boolean(data?.sms?.hasChurchCredentials) &&
    data?.sms?.mode === 'byok' &&
    data?.sms?.isConfigured;

  const handleSectionChange = (value: string) => {
    navigate(value === 'credits' ? SMS_SETTINGS_CREDITS_PATH : SMS_SETTINGS_CONNECTION_PATH);
  };

  const pageDescription =
    section === 'credits' ? t('hubtel.creditsTabDescription') : t('hubtel.connectionTabDescription');

  return (
    <div>
      <PageHeader
        title={t('hubtel.title')}
        description={pageDescription}
        headerActions={
          showMessaging ? (
            <Button onClick={openBuyCredits}>{tMessaging('sms.buyCredits')}</Button>
          ) : undefined
        }
      />

      {status.isLoading ? (
        <div className="mt-6">
          <ServiceCardSkeleton />
        </div>
      ) : !showMessaging ? (
        <Card className="mt-6">
          <CardContent className="py-8 text-center text-body text-fg-secondary">
            {t('hubtel.empty')}
          </CardContent>
        </Card>
      ) : (
        <Tabs value={section} onValueChange={handleSectionChange} className="mt-6">
          <TabsList>
            <TabsTrigger value="credits">{t('hubtel.tabs.credits')}</TabsTrigger>
            <TabsTrigger value="connection">{t('hubtel.tabs.connection')}</TabsTrigger>
          </TabsList>

          <TabsContent value="credits">
            <MessageCreditsPanel />
          </TabsContent>

          <TabsContent value="connection">
            <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
              <Card id="sms-keys" className="flex flex-col scroll-mt-6">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand/10"
                        aria-hidden
                      >
                        <Mail className="size-5 text-brand" />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <CardTitle>{t('hubtel.smsLabel')}</CardTitle>
                        <CardDescription>{t('hubtel.smsCardDescription')}</CardDescription>
                      </div>
                    </div>
                    <Badge variant={smsBadge.variant} className="shrink-0">
                      {smsBadge.label}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 space-y-4">
                  {smsConfigured && data?.sms ? (
                    <dl className="space-y-3">
                      {data.sms.senderId ? (
                        <DetailRow label={t('hubtel.senderId')} value={data.sms.senderId} />
                      ) : null}
                      {data.sms.maskedHint ? (
                        <DetailRow
                          label={t('hubtel.keysOnFile')}
                          value={
                            <span className="font-mono text-body-sm">{data.sms.maskedHint}</span>
                          }
                        />
                      ) : null}
                      {data.sms.lastValidatedAt ? (
                        <DetailRow
                          label={t('hubtel.lastChecked')}
                          value={formatDateTime(data.sms.lastValidatedAt)}
                        />
                      ) : null}
                    </dl>
                  ) : (
                    <p className="text-body-sm text-fg-secondary">{t('hubtel.smsEmpty')}</p>
                  )}

                  {smsUsingHubtel ? <Tip tipId="hubtel.smsBalance" mode="inline" /> : null}
                </CardContent>

                <CardFooter>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button onClick={openSms}>
                      {smsConfigured ? t('hubtel.updateSms') : t('hubtel.connectSms')}
                    </Button>
                  </div>
                </CardFooter>
              </Card>

              <SmsSendingOptionsCard hubtelReady={smsConfigured} onBuyCredits={openBuyCredits} />
            </div>
          </TabsContent>
        </Tabs>
      )}

      <PurchaseCreditsDialog
        open={creditsPurchaseOpen}
        onOpenChange={setCreditsPurchaseOpen}
        summary={creditsSummary.data}
      />

      <FormDialog
        open={smsOpen}
        onOpenChange={setSmsOpen}
        title={smsConfigured ? t('hubtel.updateSms') : t('hubtel.connectSms')}
        description={t('hubtel.connectSmsDescription')}
        size="md"
        closeLabel={tCommon('action.close')}
        cancelLabel={tCommon('action.cancel')}
        submitLabel={t('hubtel.saveSms')}
        submitting={saveSms.isPending}
        submitDisabled={!canSaveSms}
        onSubmit={async () => {
          await saveSms.mutateAsync();
          return;
        }}
      >
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-surface-secondary p-3 space-y-1">
            <p className="text-caption font-medium text-fg">{t('hubtel.guidelinesTitle')}</p>
            <ul className="list-disc space-y-1 pl-4 text-caption text-fg-secondary">
              <li>{t('hubtel.smsGuideline1')}</li>
              <li>{t('hubtel.smsGuideline2')}</li>
              <li>{t('hubtel.smsGuideline3')}</li>
              <li>{t('hubtel.smsGuideline4')}</li>
            </ul>
          </div>

          <div>
            <Label htmlFor="hubtel-sms-client-id">{t('hubtel.smsClientId')}</Label>
            <Input
              id="hubtel-sms-client-id"
              value={smsClientId}
              onChange={(e) => setSmsClientId(e.target.value)}
              autoComplete="off"
            />
            <p className="mt-1 text-caption text-fg-secondary">{t('hubtel.smsClientIdHelp')}</p>
          </div>
          <FormField
            label={t('hubtel.smsClientSecret')}
            tipId="payments.credentialsWriteOnly"
            popoverPortal="dialog"
          >
            {({ id }) => (
              <Input
                id={id}
                type="password"
                value={smsClientSecret}
                onChange={(e) => setSmsClientSecret(e.target.value)}
                autoComplete="new-password"
              />
            )}
          </FormField>
          <div>
            <Label htmlFor="hubtel-sender">{t('hubtel.senderId')}</Label>
            <Input
              id="hubtel-sender"
              value={senderId}
              onChange={(e) => setSenderId(e.target.value)}
              maxLength={11}
              autoComplete="off"
            />
            <p className="mt-1 text-caption text-fg-secondary">{t('hubtel.senderIdHelp')}</p>
          </div>
        </div>
      </FormDialog>

      {sessionDialogs}
    </div>
  );
};
